import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { DarkChapter } from '@/components/layout/DarkChapter';
import { SimpleReveal } from '@/components/SimpleReveal';
import { AudioWaveform } from '@/components/AudioWaveform';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { trackLead } from '@/lib/metaPixel';
import previewImage from '@/assets/365-content-ideas-preview.png';

const PAGE_TITLE = '365 Content Ideas For Artists | DK Artists';
const PAGE_DESCRIPTION =
  'Filmable, step-by-step TikTok and Instagram ideas for musicians. What to shoot, how to make it, and what to ask for. Free download.';
const CANONICAL_URL = 'https://dk-artists.com/365-content-ideas';
const DOWNLOAD_URL = '/365-Content-Ideas-DK-Artists.pdf';

export default function ContentIdeas() {
  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      </Helmet>

      <Navigation />

      <DarkChapter>
        <main className="relative z-10">
          <section className="px-6 lg:px-12 pt-24 pb-16 md:pt-32 md:pb-24">
            <div className="max-w-5xl mx-auto text-center">
              <SimpleReveal>
                <div className="mb-4 text-center flex flex-col items-center">
                  <AudioWaveform />
                  <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md px-2 lg:px-2.5 py-1 lg:py-1.5 gap-2">
                    <span className="font-mono text-[10px] lg:text-xs text-white/50 tracking-[0.15em] uppercase">
                      &gt; DK DOWNLOADS
                    </span>
                  </div>
                </div>
              </SimpleReveal>

              <SimpleReveal delay={80}>
                <h1 className="font-headline font-bold text-white text-[clamp(2.5rem,7vw,5rem)] leading-[1.05] tracking-[-0.03em] mb-4">
                  <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                    365
                  </span>{' '}
                  <span className="block md:inline">Content Ideas</span>
                </h1>
              </SimpleReveal>

              <SimpleReveal delay={160}>
                <p className="body-large text-white/60 max-w-2xl mx-auto">
                  Filmable, step-by-step ideas for TikTok and Instagram. What to shoot, how to make it, and what to ask for.
                </p>
              </SimpleReveal>

              <SimpleReveal delay={240}>
                <img
                  src={previewImage}
                  alt="Pages from the 365 Content Ideas For Musicians guide"
                  className="w-full max-w-4xl mx-auto mt-8 md:mt-10 drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
                  width={1600}
                  height={1100}
                />
              </SimpleReveal>

              <SimpleReveal delay={320}>
                <div className="flex flex-col items-center mt-8 md:mt-10">
                  <div className="inline-block group cta-pulse md:rounded-xl">
                    <Button
                      variant="hero-black-ring"
                      size="hero"
                      className="w-auto relative border border-[#0071e3]/40 shadow-[0_0_14px_rgba(0,113,227,0.18)] hover:border-[#0071e3]/60 hover:shadow-[0_0_24px_rgba(0,113,227,0.35)] transition-all duration-300 hover:scale-[1.02]"
                      asChild
                    >
                      <a
                        href={DOWNLOAD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackLead()}
                        className="inline-flex items-center gap-2"
                      >
                        Download The Content Ideas
                        <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </a>
                    </Button>
                  </div>
                  <p className="text-xs sm:text-sm text-white/60 mt-3">Free PDF. 105 pages.</p>
                </div>
              </SimpleReveal>
            </div>
          </section>
        </main>
      </DarkChapter>

      <Footer />
    </>
  );
}
